export type RegistrySortOrder = 'ASC' | 'DESC';

/** Which dot-segment of the sort path to expose as a single field key. */
export type RegistrySortFieldSegment = 'full' | 'root' | 'leaf';

/** Parsed registry / API `sort` query value (`field:type`, optional `-` / `+` prefix). */
export interface ParsedRegistrySortFieldPath {
  order: RegistrySortOrder;
  /** Path before `:type`, without leading `-` / `+`. */
  path: string;
  /** First dot segment of {@link path}. */
  rootField: string;
  /** Last dot segment of {@link path}. */
  leafField: string;
}

/**
 * Parses Angular-style registry sort (`paidAmountKass:number`, `-contragent.name:string`).
 * Does not validate the type suffix after `:`.
 */
export const parseRegistrySortFieldPath = (sort?: string): ParsedRegistrySortFieldPath | undefined => {
  if (!sort?.trim()) {
    return undefined;
  }
  const trimmed = sort.trim();
  const order: RegistrySortOrder = trimmed.startsWith('-') ? 'DESC' : 'ASC';
  const body = trimmed.replace(/^[-+]/, '');
  const path = body.split(':')[0]?.trim() ?? '';
  if (!path) {
    return undefined;
  }
  const segments = path.split('.').filter(Boolean);
  const rootField = segments[0] ?? path;
  const leafField = segments[segments.length - 1] ?? path;
  return { order, path, rootField, leafField };
};

/** Returns one field key from {@link parseRegistrySortFieldPath}; default segment is `leaf`. */
export const parseRegistrySortFieldKey = (sort?: string, segment: RegistrySortFieldSegment = 'leaf'): string | undefined => {
  const parsed = parseRegistrySortFieldPath(sort);
  if (!parsed) {
    return undefined;
  }
  switch (segment) {
    case 'full':
      return parsed.path;
    case 'root':
      return parsed.rootField;
    case 'leaf':
      return parsed.leafField;
    default:
      return parsed.leafField;
  }
};
